import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import useSubmitGuard from '../hooks/useSubmitGuard';
import useDeepLinkHighlight from '../hooks/useDeepLinkHighlight';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import {
  Search,
  Plus,
  Pencil,
  Trash2,
  Upload,
  BookOpen,
  Filter,
  X
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORIES = ['Spares', 'Tyres', 'Consumables', 'Electrical', 'Hydraulic', 'Engine', 'General'];

const EMPTY_FORM = {
  part_number: '', part_name: '', description: '', asset_class: '', door_no: '',
  make: '', model: '', uom: "No's", category: 'Spares',
  alternate_part_numbers: '', oem_reference: ''
};

export default function PartsDirectoryPage({ user }) {
  const guard = useSubmitGuard();
  const [parts, setParts] = useState([]);
  const [assetClasses, setAssetClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [classFilter, setClassFilter] = useState('all');
  const [catFilter, setCatFilter] = useState('all');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [importOpen, setImportOpen] = useState(false);

  const canEdit = user?.is_admin || user?.is_super_admin || user?.permissions?.parts_directory?.edit;

  useDeepLinkHighlight('part', 'part-row-', [loading]);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchData = async () => {
    try {
      const [partsRes, classesRes] = await Promise.all([
        axios.get(`${API}/parts-directory?limit=1000`, { headers }),
        axios.get(`${API}/parts-directory/asset-classes`, { headers }),
      ]);
      setParts(partsRes.data);
      setAssetClasses(classesRes.data);
    } catch (e) {
      toast.error('Failed to load parts directory');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line

  const filtered = useMemo(() => {
    let list = parts;
    if (classFilter !== 'all') list = list.filter(p => p.asset_class === classFilter);
    if (catFilter !== 'all') list = list.filter(p => p.category === catFilter);
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(p =>
        (p.part_number || '').toLowerCase().includes(q) ||
        (p.part_name || '').toLowerCase().includes(q) ||
        (p.description || '').toLowerCase().includes(q) ||
        (p.door_no || '').toLowerCase().includes(q) ||
        (p.make || '').toLowerCase().includes(q) ||
        (p.alternate_part_numbers || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [parts, classFilter, catFilter, searchTerm]);

  const openAdd = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM });
    setDialogOpen(true);
  };

  const openEdit = (part) => {
    setEditing(part);
    setForm({
      part_number: part.part_number || '',
      part_name: part.part_name || '',
      description: part.description || '',
      asset_class: part.asset_class || '',
      door_no: part.door_no || '',
      make: part.make || '',
      model: part.model || '',
      uom: part.uom || "No's",
      category: part.category || 'Spares',
      alternate_part_numbers: part.alternate_part_numbers || '',
      oem_reference: part.oem_reference || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.part_number || !form.part_name) {
      toast.error('Part number and part name are required');
      return;
    }
    guard(async () => {
      try {
        if (editing) {
          await axios.put(`${API}/parts-directory/${editing.id}`, form, { headers });
          toast.success('Part updated');
        } else {
          await axios.post(`${API}/parts-directory`, form, { headers });
          toast.success('Part added to directory');
        }
        setDialogOpen(false);
        fetchData();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to save part');
      }
    });
  };

  const handleDelete = async (part) => {
    if (!window.confirm(`Delete "${part.part_name}" (${part.part_number})?`)) return;
    guard(async () => {
      try {
        await axios.delete(`${API}/parts-directory/${part.id}`, { headers });
        toast.success('Part deleted');
        fetchData();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to delete');
      }
    });
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    guard(async () => {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API}/parts-directory/bulk-import`, fd, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' }
        });
        toast.success(res.data.message);
        setImportOpen(false);
        fetchData();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Import failed');
      }
    });
    e.target.value = '';
  };

  const uniqueCategories = [...new Set(parts.map(p => p.category).filter(Boolean))].sort();

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" /></div>;

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="parts-directory-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <BookOpen className="h-6 w-6" />
            Parts List Directory
          </h1>
          <p className="text-sm text-slate-500 mt-1">{parts.length} parts across {assetClasses.length} asset classes</p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setImportOpen(true)} data-testid="import-parts-btn">
              <Upload className="h-4 w-4 mr-1" /> Import CSV
            </Button>
            <Button size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={openAdd} data-testid="add-part-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Part
            </Button>
          </div>
        )}
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by part number, name, door no, make..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="parts-search-input"
          />
        </div>
        <Select value={classFilter} onValueChange={setClassFilter}>
          <SelectTrigger className="w-48" data-testid="asset-class-filter">
            <Filter className="h-3.5 w-3.5 mr-1.5 text-slate-400" />
            <SelectValue placeholder="Asset Class" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Asset Classes</SelectItem>
            {assetClasses.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-40" data-testid="category-filter">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {uniqueCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Parts', value: parts.length, color: 'bg-slate-50 border-slate-200' },
          { label: 'Asset Classes', value: assetClasses.length, color: 'bg-blue-50 border-blue-200' },
          { label: 'Categories', value: uniqueCategories.length, color: 'bg-amber-50 border-amber-200' },
          { label: 'Showing', value: filtered.length, color: 'bg-green-50 border-green-200' },
        ].map(s => (
          <div key={s.label} className={`rounded-lg border p-3 ${s.color}`}>
            <p className="text-xs text-slate-500">{s.label}</p>
            <p className="text-xl font-bold text-slate-800">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-auto max-h-[60vh]">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-semibold text-xs">Part Number</TableHead>
              <TableHead className="font-semibold text-xs">Part Name</TableHead>
              <TableHead className="font-semibold text-xs">Asset Class</TableHead>
              <TableHead className="font-semibold text-xs">Door No</TableHead>
              <TableHead className="font-semibold text-xs">Make</TableHead>
              <TableHead className="font-semibold text-xs">Model</TableHead>
              <TableHead className="font-semibold text-xs">Category</TableHead>
              <TableHead className="font-semibold text-xs">UOM</TableHead>
              {canEdit && <TableHead className="font-semibold text-xs w-20">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canEdit ? 9 : 8} className="text-center py-12 text-slate-400">
                  {searchTerm || classFilter !== 'all' || catFilter !== 'all'
                    ? 'No parts match your filters'
                    : 'No parts in directory yet. Click "Add Part" to get started.'
                  }
                </TableCell>
              </TableRow>
            ) : filtered.map(part => (
              <TableRow key={part.id} className="hover:bg-slate-50/50" data-testid={`part-row-${part.id}`}>
                <TableCell className="font-mono text-sm font-medium text-slate-800" data-testid={`part-number-${part.id}`}>{part.part_number}</TableCell>
                <TableCell>
                  <div className="text-sm font-medium text-slate-700">{part.part_name}</div>
                  {part.description && <div className="text-xs text-slate-400 truncate max-w-[200px]">{part.description}</div>}
                </TableCell>
                <TableCell><Badge variant="outline" className="text-xs">{part.asset_class || '-'}</Badge></TableCell>
                <TableCell className="text-sm">{part.door_no || '-'}</TableCell>
                <TableCell className="text-sm text-slate-600">{part.make || '-'}</TableCell>
                <TableCell className="text-sm text-slate-600">{part.model || '-'}</TableCell>
                <TableCell><Badge variant="secondary" className="text-xs">{part.category}</Badge></TableCell>
                <TableCell className="text-sm text-slate-500">{part.uom}</TableCell>
                {canEdit && (
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(part)} data-testid={`edit-part-${part.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      {(user?.is_admin || user?.is_super_admin) && (
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => handleDelete(part)} data-testid={`delete-part-${part.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Part' : 'Add New Part'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Part Number *</Label>
                <Input
                  value={form.part_number}
                  onChange={e => setForm({ ...form, part_number: e.target.value })}
                  placeholder="e.g., ACB-100-EX"
                  className="mt-1"
                  required
                  data-testid="form-part-number"
                />
              </div>
              <div>
                <Label>Part Name *</Label>
                <Input
                  value={form.part_name}
                  onChange={e => setForm({ ...form, part_name: e.target.value })}
                  placeholder="e.g., Air Compressor Belt"
                  className="mt-1"
                  required
                  data-testid="form-part-name"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Asset Class</Label>
                <Select value={form.asset_class || '__none__'} onValueChange={v => setForm({ ...form, asset_class: v === '__none__' ? '' : v })}>
                  <SelectTrigger className="mt-1" data-testid="form-asset-class">
                    <SelectValue placeholder="Select asset class" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">-- None --</SelectItem>
                    {assetClasses.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Door No</Label>
                <Input
                  value={form.door_no}
                  onChange={e => setForm({ ...form, door_no: e.target.value })}
                  placeholder="e.g., EX-01, DT-05"
                  className="mt-1"
                  data-testid="form-door-no"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Make / Brand</Label>
                <Input
                  value={form.make}
                  onChange={e => setForm({ ...form, make: e.target.value })}
                  placeholder="e.g., Caterpillar, Komatsu"
                  className="mt-1"
                  data-testid="form-make"
                />
              </div>
              <div>
                <Label>Model</Label>
                <Input
                  value={form.model}
                  onChange={e => setForm({ ...form, model: e.target.value })}
                  placeholder="e.g., 320D, PC200"
                  className="mt-1"
                  data-testid="form-model"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                  <SelectTrigger className="mt-1" data-testid="form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>UOM</Label>
                <Select value={form.uom} onValueChange={v => setForm({ ...form, uom: v })}>
                  <SelectTrigger className="mt-1" data-testid="form-uom"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["No's", "Kg", "Litre", "Metre", "Set", "Pair", "Box"].map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>OEM Reference</Label>
                <Input
                  value={form.oem_reference}
                  onChange={e => setForm({ ...form, oem_reference: e.target.value })}
                  placeholder="OEM part ref"
                  className="mt-1"
                  data-testid="form-oem-ref"
                />
              </div>
            </div>
            <div>
              <Label>Alternate Part Numbers</Label>
              <Input
                value={form.alternate_part_numbers}
                onChange={e => setForm({ ...form, alternate_part_numbers: e.target.value })}
                placeholder="e.g., ALT-100, XYZ-200 (comma separated)"
                className="mt-1"
                data-testid="form-alt-parts"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="Additional details about the part..."
                rows={2}
                className="mt-1"
                data-testid="form-description"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-part-btn">
                {editing ? 'Update Part' : 'Add Part'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Import Parts from CSV</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Upload a CSV file with columns: <span className="font-mono text-xs">part_number, part_name, description, asset_class, door_no, make, model, uom, category, alternate_part_numbers, oem_reference</span>
            </p>
            <p className="text-xs text-slate-400">Duplicate part numbers (same asset class) will be skipped.</p>
            <Input
              type="file"
              accept=".csv"
              onChange={handleImport}
              data-testid="import-csv-input"
            />
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={() => setImportOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
